// Layer 4 (UI) — the Pareto tab (a20-maintenance S2, rebuilt to the locked
// comp `downtime.html` A). It leads with the hours-against-dollars butterfly,
// then the hours KPIs and the ranked cause list with one category's sub-causes
// open. Every number comes from Causes.transform.ts and
// HoursDollars.transform.ts over the selected window; nothing is typed here.
// A sub-cause carries an FMEA reference only where the log links one.

import { useEffect, useState } from 'react';
import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { KpiCard } from '@/versions/v1/modules/_viz';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { KpiStrip } from '../maintenance/KpiStrip';
import { type DowntimeEvent } from '../maintenance/DowntimeEvents.transform';
import { type CauseCategoryId } from '../maintenance/downtime.seed';
import { PARETO_CUT_PCT } from '../maintenance/downtime-events.seed';
import { formatHours } from '../maintenance/Downtime.transform';
import { causeKpis, causeParetoWithStops, subCauseFailureModes, subCausesFor } from './Causes.transform';
import { CARD_TABLE_DENSE, DowntimeCard, RankBar, SYN_CHIP, THREE_UP } from './DowntimeCard';
import type { DowntimeImpactRead } from './DowntimeImpact.transform';
import { HoursDollars } from './HoursDollars';

/** Label, bar and figures on one line (comp `.brow`): fixed outer tracks so
 *  every row's bar starts and ends at the same x and lengths compare. */
const BAR_ROW = 'grid w-full grid-cols-[14rem_minmax(0,1fr)_12rem] items-center gap-x-3 rounded-md px-1 py-1 text-left text-sm';

export function CausesTab({ rows, impactRead, selectedCause }: { rows: readonly DowntimeEvent[]; impactRead: DowntimeImpactRead; selectedCause?: CauseCategoryId }) {
  const pareto = causeParetoWithStops(rows);
  const kpis = causeKpis(rows);
  const [openId, setOpenId] = useState<CauseCategoryId | undefined>(selectedCause ?? pareto[0]?.causeCategory);
  const topHours = pareto[0]?.hours ?? 0;

  useEffect(() => {
    setOpenId(selectedCause && pareto.some((row) => row.causeCategory === selectedCause)
      ? selectedCause
      : pareto[0]?.causeCategory);
  }, [rows, selectedCause]);

  return (
    <Commentable name="Causes">
      <div className="space-y-3">
        <HoursDollars rows={rows} windowLabel={impactRead.window.rangeLabel} />
        <div className={THREE_UP}>
          <KpiStrip>
            <KpiCard
              label="Unplanned equipment hours"
              value={formatHours(kpis.unplannedHours)}
              sub={`${impactRead.window.rangeLabel} · across maintainable items`}
              tone="bad"
              className="px-3.5 py-3"
            />
            <KpiCard
              label="Unplanned stops"
              value={String(kpis.eventCount)}
              sub="equipment events in the selected window"
              tone="neutral"
              className="px-3.5 py-3"
            />
            <KpiCard
              label="Top 3 by hours"
              value={`${Math.round(kpis.topThreeSharePct)}%`}
              sub={`of ${formatHours(kpis.unplannedHours)} equipment downtime`}
              tone="warn"
              className="px-3.5 py-3"
            />
          </KpiStrip>
        </div>
        <DowntimeCard
          title="Unplanned hours by cause"
          meta={`${impactRead.window.rangeLabel} · ranked by outage hours`}
          actions={SYN_CHIP}
        >
          <div>
            {pareto.map((row, index) => {
              const open = row.causeCategory === openId;
              const cutEndsHere = row.withinCut && !pareto[index + 1]?.withinCut && index < pareto.length - 1;
              return (
                <div key={row.causeCategory}>
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpenId(row.causeCategory)}
                    className={cn(BAR_ROW, 'transition-colors duration-150 hover:bg-[var(--surface-1)]')}
                  >
                    <span className="flex min-w-0 items-baseline gap-2">
                      <span className="w-3 shrink-0 font-semibold tabular-nums text-muted">{index + 1}</span>
                      <span className="truncate font-medium text-ink-primary">{row.label}</span>
                    </span>
                    <RankBar
                      ratio={topHours > 0 ? row.hours / topHours : 0}
                      tone={index === 0 ? 'top' : row.withinCut ? 'ink' : 'dim'}
                    />
                    <span className="whitespace-nowrap text-right tabular-nums">
                      <span className="font-semibold text-ink-primary">{formatHours(row.hours)}</span>
                      <span className="ml-2 text-xs text-muted">{row.eventCount} ev · {Math.round(row.cumulativePct)}% cum</span>
                    </span>
                  </button>

                  {open ? (
                    <SubCauseTable
                      rows={subCausesFor(rows, row.causeCategory)}
                      failureModes={subCauseFailureModes(rows, row.causeCategory)}
                    />
                  ) : null}

                  {cutEndsHere ? (
                    <div className="relative my-1.5 border-t border-dashed border-[var(--hairline-strong)]">
                      <span className="absolute -top-2 right-0 bg-[var(--surface-2)] pl-1.5 text-[10px] text-muted">
                        {PARETO_CUT_PCT}% of hours
                      </span>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </DowntimeCard>
      </div>
    </Commentable>
  );
}

function SubCauseTable({
  rows,
  failureModes,
}: {
  rows: ReturnType<typeof subCausesFor>;
  failureModes: ReturnType<typeof subCauseFailureModes>;
}) {
  const top = rows[0]?.pctOfCategory ?? 0;
  return (
    <div className={cn(CARD_TABLE_DENSE, 'mb-1 ml-6 mt-1 border-l border-[var(--hairline-strong)] pl-3.5')}>
      <Table wrap={false} className="tabular-nums">
        <THead>
          <TR>
            <TH>Sub-cause</TH>
            <TH className="text-right">Hours</TH>
            <TH className="w-[36%]">% of category</TH>
            <TH className="text-right">Events</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((row) => {
            const mode = failureModes.get(row.subCause);
            return (
              <TR key={row.subCause}>
                <TD className="font-medium !text-ink-primary">
                  {row.subCause}
                  {mode ? (
                    <span
                      data-fmea-ref={mode.failureModeId}
                      className="ml-2 inline-flex whitespace-nowrap rounded-full border border-dashed border-[var(--hairline-strong)] px-1.5 align-[1px] text-[10px] font-normal tracking-[0.02em] text-muted"
                    >
                      {mode.failureModeId} · RPN {mode.rpn}
                    </span>
                  ) : null}
                </TD>
                <TD className="whitespace-nowrap text-right">{formatHours(row.hours)}</TD>
                <TD>
                  <span className="flex items-center gap-2">
                    <RankBar className="flex-1" ratio={top > 0 ? row.pctOfCategory / top : 0} />
                    <span className="w-8 shrink-0 text-right text-xs text-muted">{Math.round(row.pctOfCategory)}%</span>
                  </span>
                </TD>
                <TD className="text-right">{row.eventCount}</TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}
